/**
 * 统一错误处理工具
 * 提供标准化的错误消息处理和用户友好的错误展示
 */
import { message, notification } from "antd";

/**
 * 错误类型枚举
 */
export const ERROR_TYPES = {
    NETWORK: "NETWORK",           // 网络错误
    VALIDATION: "VALIDATION",     // 数据验证错误
    BUSINESS: "BUSINESS",         // 业务逻辑错误
    PERMISSION: "PERMISSION",     // 权限错误
    SERVER: "SERVER",             // 服务器错误
    UNKNOWN: "UNKNOWN"            // 未知错误
};

/**
 * 错误严重程度
 */
export const ERROR_SEVERITY = {
    LOW: "LOW",       // 低级错误，使用message提示
    MEDIUM: "MEDIUM", // 中级错误，使用warning notification
    HIGH: "HIGH",     // 高级错误，使用error notification
    CRITICAL: "CRITICAL" // 严重错误，使用error notification + 可能需要页面跳转
};

const stringifyErrorDetail = (detail) => {
    if (typeof detail === "string") return detail;

    if (Array.isArray(detail)) {
        return detail
            .map((item) => {
                if (typeof item === "string") return item;
                if (item && typeof item === "object") {
                    const location = Array.isArray(item.loc) ? item.loc.join(".") : null;
                    const itemMessage = stringifyErrorDetail(item.msg ?? item.message ?? item.detail);
                    return [location, itemMessage].filter(Boolean).join(": ");
                }
                return item == null ? "" : String(item);
            })
            .filter(Boolean)
            .join("；");
    }

    if (detail && typeof detail === "object") {
        const nestedMessage = detail.message ?? detail.msg ?? detail.detail ?? detail.error;
        if (nestedMessage !== undefined && nestedMessage !== detail) {
            return stringifyErrorDetail(nestedMessage);
        }
        try {
            return JSON.stringify(detail);
        } catch (_error) {
            return String(detail);
        }
    }

    return detail == null ? "" : String(detail);
};

/**
 * 从axios错误对象中提取详细错误信息
 * @param {Object} error - axios错误对象
 * @returns {Object} 标准化的错误信息
 */
export const extractErrorInfo = (error) => {
    const errorInfo = {
        type: ERROR_TYPES.UNKNOWN,
        severity: ERROR_SEVERITY.MEDIUM,
        code: null,
        message: "未知错误",
        details: null,
        originalError: error
    };

    if (!error) {
        return errorInfo;
    }

    // 网络错误（请求未到达服务器）
    if (!error.response) {
        errorInfo.type = ERROR_TYPES.NETWORK;
        errorInfo.severity = ERROR_SEVERITY.HIGH;
        errorInfo.message = "网络连接异常，请检查网络连接后重试";
        return errorInfo;
    }

    const { status, data } = error.response;
    errorInfo.code = status;
    const detailMessage = stringifyErrorDetail(data?.detail);
    const responseMessage = detailMessage || stringifyErrorDetail(data?.message);

    // 根据HTTP状态码分类错误
    switch (status) {
        case 400:
            errorInfo.type = ERROR_TYPES.VALIDATION;
            errorInfo.severity = ERROR_SEVERITY.MEDIUM;
            // 尝试提取具体的业务错误信息
            if (detailMessage) {
                // 检查是否是时间逻辑错误等业务错误
                if (detailMessage.includes("时间逻辑错误") || detailMessage.includes("时间设置无效")) {
                    errorInfo.type = ERROR_TYPES.BUSINESS;
                    errorInfo.message = detailMessage;
                } else {
                    errorInfo.message = detailMessage;
                }
            } else if (responseMessage) {
                errorInfo.message = responseMessage;
            } else {
                errorInfo.message = "请求参数错误";
            }
            break;

        case 401:
            errorInfo.type = ERROR_TYPES.PERMISSION;
            errorInfo.severity = ERROR_SEVERITY.CRITICAL;
            errorInfo.message = "登录已过期，请重新登录";
            break;

        case 403:
            errorInfo.type = ERROR_TYPES.PERMISSION;
            errorInfo.severity = ERROR_SEVERITY.HIGH;
            errorInfo.message = "无权限访问该资源";
            break;

        case 404:
            errorInfo.type = ERROR_TYPES.VALIDATION;
            errorInfo.severity = ERROR_SEVERITY.MEDIUM;
            errorInfo.message = "请求的资源不存在";
            break;

        case 409:
            errorInfo.type = ERROR_TYPES.BUSINESS;
            errorInfo.severity = ERROR_SEVERITY.MEDIUM;
            errorInfo.message = responseMessage || "操作冲突，请刷新后重试";
            break;

        case 422:
            errorInfo.type = ERROR_TYPES.VALIDATION;
            errorInfo.severity = ERROR_SEVERITY.MEDIUM;
            // FlyMeta 自定义校验响应：{ message, validation_errors: [{ field, message }] }
            // FastAPI 默认形态：{ detail: [...] } 或 { detail: "..." }
            {
                const validationErrors = Array.isArray(data?.validation_errors) ? data.validation_errors : null;
                const validationSummary = validationErrors
                    ? validationErrors
                          .map((item) => {
                              if (!item || typeof item !== "object") return "";
                              const field = item.field || (Array.isArray(item.loc) ? item.loc.join(".") : "");
                              const itemMessage = stringifyErrorDetail(item.message ?? item.msg);
                              return [field, itemMessage].filter(Boolean).join(": ");
                          })
                          .filter(Boolean)
                          .join("；")
                    : "";

                if (validationSummary) {
                    errorInfo.message = validationSummary;
                } else if (detailMessage) {
                    errorInfo.message = detailMessage;
                    if (detailMessage.includes("时间") || detailMessage.includes("日期")) {
                        errorInfo.type = ERROR_TYPES.BUSINESS;
                    }
                } else if (responseMessage) {
                    errorInfo.message = responseMessage;
                } else {
                    errorInfo.message = "数据验证失败";
                }
            }
            break;

        case 500:
            errorInfo.type = ERROR_TYPES.SERVER;
            errorInfo.severity = ERROR_SEVERITY.HIGH;
            errorInfo.message = "服务器内部错误，请稍后重试";
            break;

        default:
            errorInfo.type = ERROR_TYPES.UNKNOWN;
            errorInfo.severity = ERROR_SEVERITY.MEDIUM;
            errorInfo.message = responseMessage || `请求失败 (${status})`;
    }

    // 保存详细信息用于调试
    errorInfo.details = data;

    return errorInfo;
};

/**
 * 显示错误信息给用户
 * @param {Object} errorInfo - 标准化的错误信息
 * @param {Object} options - 显示选项
 */
export const showError = (errorInfo, options = {}) => {
    const {
        showNotification = false,  // 是否使用notification而不是message
        duration = 4.5,           // 显示时长
        placement = "topRight",   // notification位置
        silent = false            // 是否静默处理（不显示UI提示）
    } = options;

    if (silent) {
        return;
    }

    const { type, severity, message: errorMessage, code } = errorInfo;

    // 构建显示消息
    const displayMessage = code ? `${errorMessage} [${code}]` : errorMessage;

    // 根据严重程度选择显示方式
    if (showNotification || severity === ERROR_SEVERITY.HIGH || severity === ERROR_SEVERITY.CRITICAL) {
        const notificationConfig = {
            message: getErrorTitle(type),
            description: displayMessage,
            duration,
            placement
        };

        if (severity === ERROR_SEVERITY.CRITICAL || severity === ERROR_SEVERITY.HIGH) {
            notification.error(notificationConfig);
        } else {
            notification.warning(notificationConfig);
        }
    } else {
        // 使用message显示
        if (severity === ERROR_SEVERITY.LOW) {
            message.info(displayMessage, duration);
        } else {
            message.error(displayMessage, duration);
        }
    }
};

/**
 * 根据错误类型获取错误标题
 * @param {string} errorType - 错误类型
 * @returns {string} 错误标题
 */
const getErrorTitle = (errorType) => {
    switch (errorType) {
        case ERROR_TYPES.NETWORK:
            return "网络错误";
        case ERROR_TYPES.VALIDATION:
            return "数据验证错误";
        case ERROR_TYPES.BUSINESS:
            return "操作失败";
        case ERROR_TYPES.PERMISSION:
            return "权限错误";
        case ERROR_TYPES.SERVER:
            return "服务器错误";
        default:
            return "系统错误";
    }
};

/**
 * 统一的错误处理函数
 * @param {Object} error - 错误对象
 * @param {Object} options - 处理选项
 * @returns {Object} 标准化的错误信息
 */
export const handleError = (error, options = {}) => {
    const errorInfo = extractErrorInfo(error);
    
    // 在开发环境下打印详细错误信息
    if (process.env.NODE_ENV === "development") {
        console.group(`🚨 错误处理: ${errorInfo.type}`);
        console.error("错误信息:", errorInfo);
        console.error("原始错误:", error);
        console.groupEnd();
    }

    // 显示错误信息
    showError(errorInfo, options);

    return errorInfo;
};

/**
 * 创建特定上下文的错误处理器
 * @param {string} context - 上下文名称（如"航班信息更新"）
 * @param {Object} defaultOptions - 默认选项
 * @returns {Function} 错误处理函数
 */
export const createErrorHandler = (context, defaultOptions = {}) => {
    return (error, options = {}) => {
        const mergedOptions = { ...defaultOptions, ...options };
        const errorInfo = handleError(error, mergedOptions);
        
        // 添加上下文信息到错误日志
        if (process.env.NODE_ENV === "development") {
            console.log(`错误上下文: ${context}`);
        }
        
        return errorInfo;
    };
};

/**
 * 业务操作结果处理器
 * @param {Promise} operation - 业务操作Promise
 * @param {Object} options - 处理选项
 * @returns {Promise} 处理结果
 */
export const handleAsyncOperation = async (operation, options = {}) => {
    const {
        successMessage = null,
        errorContext = "操作",
        showSuccessMessage = true,
        ...errorOptions
    } = options;

    try {
        const result = await operation;
        
        // 显示成功消息
        if (showSuccessMessage && successMessage) {
            message.success(successMessage);
        }
        
        return { success: true, data: result, error: null };
    } catch (error) {
        const errorInfo = handleError(error, {
            ...errorOptions,
            context: errorContext
        });
        
        return { success: false, data: null, error: errorInfo };
    }
};

export default {
    ERROR_TYPES,
    ERROR_SEVERITY,
    extractErrorInfo,
    showError,
    handleError,
    createErrorHandler,
    handleAsyncOperation
};
